import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, companyMenuItems, candidateMenuItems } from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { chatAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  MessageSquare, 
  Send, 
  Loader2,
  User,
  Building2,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

export default function Chats() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const menuItems = user?.role === 'COMPANY' ? companyMenuItems : candidateMenuItems;

  useEffect(() => {
    fetchChats();
  }, []);

  useEffect(() => {
    if (selectedChat) {
      fetchMessages(selectedChat.id);
    }
  }, [selectedChat]);

  const fetchChats = async () => {
    try {
      const response = await chatAPI.getChats();
      setChats(response.data);
    } catch (error) {
      toast.error('Erro ao carregar conversas');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (chatId) => {
    try {
      const response = await chatAPI.getMessages(chatId);
      setMessages(response.data);
    } catch (error) {
      toast.error('Erro ao carregar mensagens');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChat) return;

    setSending(true);
    try {
      const response = await chatAPI.sendMessage(selectedChat.id, newMessage);
      setMessages([...messages, response.data]);
      setNewMessage('');
    } catch (error) {
      toast.error('Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };

  return (
    <DashboardLayout menuItems={menuItems}>
      <div className="h-[calc(100vh-180px)]" data-testid="chats-page">
        <div className="grid md:grid-cols-3 gap-6 h-full">
          {/* Chat List */}
          <Card className="border-slate-200 md:col-span-1">
            <CardContent className="p-0 h-full flex flex-col">
              <div className="p-4 border-b border-slate-200">
                <h2 className="font-semibold text-slate-900">Conversas</h2>
              </div>
              
              {loading ? (
                <div className="flex items-center justify-center flex-1">
                  <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
                </div>
              ) : chats.length === 0 ? (
                <div className="flex flex-col items-center justify-center flex-1 p-4 text-center">
                  <MessageSquare className="w-10 h-10 text-slate-300 mb-2" />
                  <p className="text-sm text-slate-600">Nenhuma conversa ainda</p>
                </div>
              ) : (
                <ScrollArea className="flex-1">
                  {chats.map((chat) => (
                    <button
                      key={chat.id}
                      onClick={() => setSelectedChat(chat)}
                      className={`w-full p-4 text-left border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                        selectedChat?.id === chat.id ? 'bg-cyan-50' : ''
                      }`}
                      data-testid={`chat-item-${chat.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          user?.role === 'COMPANY' ? 'bg-emerald-100' : 'bg-cyan-100'
                        }`}>
                          {user?.role === 'COMPANY' ? (
                            <User className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <Building2 className="w-5 h-5 text-cyan-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">
                            {user?.role === 'COMPANY' 
                              ? (chat.candidate_name || 'Candidato')
                              : (chat.company_name || 'Empresa')}
                          </p>
                          <p className="text-xs text-slate-500">
                            Clique para ver mensagens
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </ScrollArea>
              )}
            </CardContent>
          </Card>

          {/* Chat Messages */}
          <Card className="border-slate-200 md:col-span-2">
            <CardContent className="p-0 h-full flex flex-col">
              {selectedChat ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-slate-200 flex items-center gap-3">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="md:hidden"
                      onClick={() => setSelectedChat(null)}
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      user?.role === 'COMPANY' ? 'bg-emerald-100' : 'bg-cyan-100'
                    }`}>
                      {user?.role === 'COMPANY' ? (
                        <User className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <Building2 className="w-5 h-5 text-cyan-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {user?.role === 'COMPANY' 
                          ? (selectedChat.candidate_name || 'Candidato')
                          : (selectedChat.company_name || 'Empresa')}
                      </p>
                    </div>
                  </div>

                  {/* Messages */}
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-4">
                      {messages.map((message) => (
                        <div
                          key={message.id}
                          className={`flex ${
                            message.sender_role === user?.role ? 'justify-end' : 'justify-start'
                          }`}
                        >
                          <div
                            className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                              message.sender_role === user?.role
                                ? 'bg-cyan-600 text-white rounded-br-md'
                                : 'bg-slate-100 text-slate-900 rounded-bl-md'
                            }`}
                          >
                            <p className="text-sm">{message.message}</p>
                            <p className={`text-xs mt-1 ${
                              message.sender_role === user?.role ? 'text-cyan-100' : 'text-slate-500'
                            }`}>
                              {new Date(message.created_at).toLocaleTimeString('pt-BR', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>

                  {/* Message Input */}
                  <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-200">
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Digite sua mensagem..."
                        disabled={sending}
                        data-testid="message-input"
                      />
                      <Button 
                        type="submit"
                        className="bg-cyan-600 hover:bg-cyan-700"
                        disabled={sending || !newMessage.trim()}
                        data-testid="send-message-btn"
                      >
                        {sending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center p-4">
                  <MessageSquare className="w-16 h-16 text-slate-300 mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">
                    Selecione uma conversa
                  </h3>
                  <p className="text-slate-600">
                    Escolha uma conversa na lista para ver as mensagens
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
